import React, {useMemo, useState} from "react";
import {App, Button, Card, Empty, Form, Input, Modal, Select, Typography,} from "antd";
import {
  CreditCardOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";

const {Text} = Typography;

export default function PayoutsCard({
                                      loading = false,
                                      rows = [],
                                      onCreate,
                                      onUpdate,
                                      onDelete,
                                      types = [],
                                      payMethods = [],
                                      payMethodsLoading = false,
                                    }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const {modal} = App.useApp();

  const payMethodLabelById = useMemo(() => {
    const m = new Map();
    (payMethods || []).forEach((o) => m.set(o.value, o.label));
    return m;
  }, [payMethods]);

  const sortedRows = useMemo(() => {
    return [...(rows || [])].sort((a, b) =>
      String(a.type || "").localeCompare(String(b.type || ""))
    );
  }, [rows]);

  const startCreate = () => {
    setEditing(null);
    form.resetFields();
    setOpen(true);
  };

  const startEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      pay_method_id: row?.pay_method?.id ?? row?.pay_method_id ?? row?.pay_method ?? null,
      type: row?.type ?? null,
      detail: row?.detail ?? "",
    });
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const submit = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const ok = editing?.id
        ? await onUpdate?.(editing.id, values)
        : await onCreate?.(values);

      if (ok) {
        close();
      }
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (row) => {
    modal.confirm({
      title: "Удалить реквизит?",
      icon: <ExclamationCircleOutlined/>,
      content: (
        <div className="mt-2">
          <div className="text-[#0B1B3A] font-semibold">{row?.type ?? "—"}</div>
          <div className="text-[#5C607C] break-words">{row?.detail ?? "—"}</div>
        </div>
      ),
      okText: "Удалить",
      okType: "danger",
      cancelText: "Отмена",
      onOk: () => onDelete?.(row.id),
    });
  };

  return (
    <section className="pb-8">
      <div className="bg-white rounded-[20px] shadow-lg p-4 lg:p-6">
        <div className="flex items-center justify-between pb-4 border-b">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-9 h-9 shrink-0 rounded-full bg-blue-100 flex items-center justify-center text-[var(--blue-3)]">
              <CreditCardOutlined/>
            </div>

            <div className="min-w-0">
              <h2 className="m-0 font-semibold text-[16px] lg:text-[20px] text-[#1A1A1A] truncate">
                Реквизиты
              </h2>
              <Text type="secondary" className="block break-words">
                Управление реквизитами для получения/выплат
              </Text>
            </div>
          </div>

          <Button
            type="default"
            icon={<PlusOutlined/>}
            onClick={startCreate}
            className="h-[38px] px-5 border border-[#D9D9D9] rounded-[10px] bg-white"
          >
            Добавить
          </Button>
        </div>

        <div className="mt-4">
          {loading ? (
            <Text type="secondary">Загрузка...</Text>
          ) : sortedRows.length === 0 ? (
            <Empty description="Реквизитов пока нет"/>
          ) : (
            <div className="flex flex-col gap-3">
              {sortedRows.map((r) => (
                <Card
                  key={r.id}
                  size="small"
                  styles={{body: {padding: 12}}}
                  className="rounded-[14px]"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-semibold text-[#0B1B3A] whitespace-nowrap">
                        {r.type || "—"}
                      </div>
                      <div className="text-[#5C607C] break-words">
                        {r?.pay_method
                          ? `${r.pay_method.currency} · ${r.pay_method?.method?.title ?? r.pay_method?.method?.code ?? "—"}`
                          : payMethodLabelById.get(r?.pay_method_id ?? r?.pay_method) || "—"}
                      </div>
                      <div className="text-[#5C607C] break-words">
                        {r.detail || "—"}
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      <Button
                        type="text"
                        icon={<EditOutlined/>}
                        onClick={() => startEdit(r)}
                      />
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined/>}
                        onClick={() => confirmDelete(r)}
                      />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        <Modal
          title={editing ? "Редактировать реквизит" : "Добавить реквизит"}
          open={open}
          onCancel={close}
          onOk={submit}
          okText={editing ? "Сохранить" : "Создать"}
          cancelText="Отмена"
          confirmLoading={saving}
          destroyOnHidden
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="pay_method_id"
              label="Валюта + метод"
              rules={[{required: true, message: "Выбери валюту и метод"}]}
            >
              <Select
                placeholder="Выбери валюту и метод"
                options={payMethods}
                loading={payMethodsLoading}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>

            <Form.Item
              name="type"
              label="Тип"
              rules={[{required: true, message: "Укажи тип реквизита"}]}
            >
              <Select placeholder="Выбери тип" options={types}/>
            </Form.Item>

            <Form.Item
              name="detail"
              label="Детали"
              rules={[{required: true, message: "Заполни детали реквизита"}]}
            >
              <Input.TextArea
                rows={4}
                placeholder="Например: номер карты, номер телефона..."
              />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </section>
  );
}